// components/AI/AIAgentModels.js — pilih model LLM untuk profil Agent SDK (/agent @mind, @sylor).
// Daftar model di-fetch langsung dari /v1/models masing-masing gateway (lewat host DR);
// perubahan ditulis ke /etc/agent-sdk/profiles/<profil>.env dan berlaku di permintaan berikutnya.
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../context/AuthContext';
import { Bot, Loader2, RefreshCw, Save, CheckCircle2, XCircle, Wallet } from 'lucide-react';

function ProfileCard({ p, credit, onSaved }) {
  const [model, setModel] = useState(p.current || '');
  const [small, setSmall] = useState(p.small || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const smallInvalid = !small || /[<>]/.test(small) || (p.models.length && !p.models.includes(small));
  const dirty = model !== p.current || small !== p.small;

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const { data } = await api.put('/api/agent-models', { profile: p.profile, model, small: small || undefined });
      setMsg({ ok: true, text: `Saved — @${data.profile} now uses ${data.current}` });
      onSaved && onSaved();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.error || e.message });
    } finally { setSaving(false); }
  };

  const opts = (cur) => {
    const list = p.models.slice();
    if (cur && !list.includes(cur)) list.unshift(cur);
    return list.map(m => <option key={m} value={m}>{m}{p.models.includes(m) ? '' : ' (not in gateway list)'}</option>);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bot size={18} className="text-brand-600" />
          <span className="font-semibold text-slate-800">@{p.profile}</span>
          <span className="text-xs text-slate-500">{p.models.length} models available</span>
        </div>
        {credit && (
          <span className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg ${credit.availableUsd != null && credit.availableUsd < 10 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
            <Wallet size={13} />
            {credit.availableUsd != null ? `$${credit.availableUsd} left` : `used $${credit.usedUsd}`}
          </span>
        )}
      </div>
      <label className="block text-xs font-medium text-slate-600 mb-1">Main model</label>
      <select value={model} onChange={e => setModel(e.target.value)}
        className="w-full mb-3 rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
        {opts(model)}
      </select>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        Small/fast model <span className="font-normal text-slate-400">(used by the SDK for light background tasks)</span>
      </label>
      <select value={small} onChange={e => setSmall(e.target.value)}
        className={`w-full rounded-lg border px-2 py-1.5 text-sm ${smallInvalid ? 'border-amber-400' : 'border-slate-300'}`}>
        {!small && <option value="">— not set —</option>}
        {opts(small)}
      </select>
      {smallInvalid && <p className="mt-1 text-xs text-amber-700">Current value is not a valid model on this gateway — pick one.</p>}
      <div className="mt-3 flex items-center gap-3">
        <button onClick={save} disabled={!dirty || saving || !model}
          className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
        {msg && <span className={`flex items-center gap-1 text-xs ${msg.ok ? 'text-emerald-700' : 'text-red-600'}`}>
          {msg.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}{msg.text}</span>}
      </div>
    </div>
  );
}

export default function AIAgentModels() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const { data } = await api.get('/api/agent-models'); setData(data); }
    catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const creditOf = (name) => (data?.credit?.profiles || []).find(c => c.profile === name);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-semibold text-slate-800">Agent SDK models</h3>
          <p className="text-sm text-slate-500">
            Model used by <code>/agent @mind</code> and <code>/agent @sylor</code> in AI Coding. The list is fetched live from each gateway;
            a change applies from the next /agent request, no restart needed.
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>
      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && !data && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Fetching models from gateways…</div>}
      {data && (
        <div className="grid gap-4 md:grid-cols-2">
          {data.profiles.map(p => <ProfileCard key={p.profile + p.current + p.small} p={p} credit={creditOf(p.profile)} onSaved={load} />)}
        </div>
      )}
      {data?.credit?.checkedAt && <p className="text-xs text-slate-400">Credit checked {new Date(data.credit.checkedAt).toLocaleString()}</p>}
    </div>
  );
}
